import { Button } from '@/components/ui/Button';
import type { BookView } from '@/types/api';

/**
 * Screen 1 — in-wizard recap of the book.
 *
 * This is *not* the catalog detail page — it's the small confirmation
 * card the wizard renders before launching into goal-setting. Keeps the
 * reader oriented ("yes, this is the book I tapped") and gives them an
 * out via "Back to library".
 *
 * The metadata block intentionally drops `gradeLevel` (per
 * READING_TRACKER_PLAN.md §2.1 — no `gradeLevel` field). Topics are
 * derived from the existing `genres` + `topics` label buckets and joined
 * with commas.
 */
export interface BookDetailScreenProps {
  book: BookView;
  onStart: () => void;
  onBack: () => void;
}

export function BookDetailScreen({ book, onStart, onBack }: BookDetailScreenProps) {
  const topicLabels = [...book.genres, ...book.topics].map((l) => l.name);
  const topicLine = topicLabels.length > 0 ? topicLabels.join(', ') : null;

  return (
    <section className="rounded-3xl border border-or-slate-200 bg-or-blue-100 p-6 shadow-sm">
      <div className="flex flex-col gap-6 sm:flex-row">
        {/* Cover */}
        <div className="flex shrink-0 items-start justify-center sm:justify-start">
          {book.bookCover ? (
            <img
              src={book.bookCover}
              alt={`${book.title} cover`}
              className="h-[180px] w-[130px] rounded-xl object-cover shadow-md"
            />
          ) : (
            <div
              aria-hidden
              className="flex h-[180px] w-[130px] flex-col items-center justify-center gap-1 rounded-xl bg-gradient-to-br from-or-orange-700 via-or-orange-500 to-or-orange-700 p-3 text-center text-white shadow-md"
            >
              <span className="font-display text-[14px] font-bold leading-tight">
                {book.title}
              </span>
              {book.author ? (
                <span className="text-[10px] opacity-80">{book.author}</span>
              ) : null}
            </div>
          )}
        </div>

        {/* Metadata + CTAs */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <h2 className="font-display text-[24px] font-bold leading-tight text-or-slate-900">
            {book.title}
          </h2>
          <dl className="grid grid-cols-1 gap-1 text-[13px] text-or-slate-700 sm:grid-cols-[auto_1fr] sm:gap-x-3">
            {book.author ? (
              <>
                <dt className="font-bold">Author</dt>
                <dd>{book.author}</dd>
              </>
            ) : null}
            {book.pages != null ? (
              <>
                <dt className="font-bold">Pages</dt>
                <dd>{book.pages}</dd>
              </>
            ) : null}
            {book.chapters != null ? (
              <>
                <dt className="font-bold">Chapters</dt>
                <dd>{book.chapters}</dd>
              </>
            ) : null}
            {topicLine ? (
              <>
                <dt className="font-bold">Topics</dt>
                <dd>{topicLine}</dd>
              </>
            ) : null}
          </dl>

          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <Button type="button" size="lg" onClick={onStart}>
              Start Book! 🎯
            </Button>
            <Button type="button" variant="secondary" size="lg" onClick={onBack}>
              Back to library
            </Button>
          </div>

          <p className="mt-1 text-[12px] text-or-slate-500">
            Tap <strong>Start Book!</strong> to set your reading goal and build a daily plan.
          </p>
        </div>
      </div>
    </section>
  );
}
